import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// Conditionally import project data - this will be undefined in main branch builds
let getProjectOrder;
try {
    const projectData = require('../data/projects-latest.js');
    getProjectOrder = projectData.getProjectOrder;
} catch (error) {
    // In main branch, project data is not available
    getProjectOrder = () => [];
}

const DensityContext = createContext();

export const useDensity = () => {
    const context = useContext(DensityContext);
    if (!context) {
        throw new Error('useDensity must be used within a DensityProvider');
    }
    return context;
};

export const DensityProvider = ({ children }) => {
    const [currentDensity, setCurrentDensity] = useState('medium');
    const [isTransitioning, setIsTransitioning] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    // Map time values to density keys
    const timeToDensity = {
        '1min': 'low',
        '4min': 'medium',
        '8min': 'high'
    };

    // Map density keys to time values
    const densityToTime = {
        'low': '1min',
        'medium': '4min',
        'high': '8min'
    };

    // Check if current page is a project page
    const isProjectPage = useCallback(() => {
        const projectSlugs = getProjectOrder();
        const currentSlug = location.pathname.slice(1); // Remove leading slash
        return projectSlugs.includes(currentSlug);
    }, [location.pathname]);

    // Get density from URL parameter or localStorage
    const getDensityFromUrl = useCallback(() => {
        const urlParams = new URLSearchParams(location.search);
        const timeParam = urlParams.get('time');
        if (timeParam && timeToDensity[timeParam]) {
            return timeToDensity[timeParam];
        }
        return null;
    }, [location.search]);

    // Ensure URL always has a time parameter (only on project pages)
    const ensureUrlParameter = useCallback((density) => {
        // Only add URL parameter on project pages
        if (!isProjectPage()) {
            return;
        }

        const urlParams = new URLSearchParams(location.search);
        const currentTimeParam = urlParams.get('time');
        const expectedTimeParam = densityToTime[density];
        
        // If URL doesn't have the correct time parameter, update it
        if (currentTimeParam !== expectedTimeParam) {
            urlParams.set('time', expectedTimeParam);
            const newUrl = `${location.pathname}?${urlParams.toString()}`;
            navigate(newUrl, { replace: true });
        }
    }, [isProjectPage, location.pathname, location.search, navigate]);

    // Load density preference from URL or localStorage on mount
    useEffect(() => {
        const urlDensity = getDensityFromUrl();
        const savedDensity = localStorage.getItem('contentDensity');
        
        let densityToUse = 'medium'; // default
        
        if (urlDensity) {
            densityToUse = urlDensity;
        } else if (savedDensity && ['low', 'medium', 'high'].includes(savedDensity)) {
            densityToUse = savedDensity;
        }
        
        setCurrentDensity(densityToUse);
        
        // Ensure URL always has the correct time parameter (only on project pages)
        ensureUrlParameter(densityToUse);
    }, [getDensityFromUrl, ensureUrlParameter]);

    // Update URL when density changes (only on project pages)
    useEffect(() => {
        ensureUrlParameter(currentDensity);
    }, [currentDensity, ensureUrlParameter]);

    const changeDensity = (newDensity) => {
        if (newDensity === currentDensity) return;
        
        // Save to localStorage
        localStorage.setItem('contentDensity', newDensity);
        
        // Update URL with time parameter (only on project pages)
        if (isProjectPage()) {
            const urlParams = new URLSearchParams(location.search);
            urlParams.set('time', densityToTime[newDensity]);
            
            const newUrl = `${location.pathname}?${urlParams.toString()}`;
            navigate(newUrl, { replace: true });
        }
        
        // Set transitioning state for blur effect
        setIsTransitioning(true);
        
        // Update density immediately to avoid visual glitch
        setCurrentDensity(newDensity);
        
        // Remove blur effect after a short delay
        setTimeout(() => {
            setIsTransitioning(false);
        }, 300);
    };

    const value = {
        currentDensity,
        isTransitioning,
        changeDensity
    };

    return (
        <DensityContext.Provider value={value}>
            {children}
        </DensityContext.Provider>
    );
};
